import { match } from "ts-pattern";

import {
  IncidentTypeSchema,
  type SupportedIncidentType,
} from "../../types/incidentType.js";
import type { ActionInput } from "../ActionInput.js";
import { getSupportedIncidentTypeSelection } from "./getIncidentTypeSelection.js";

/**
 * Gets the appropriate i18next translation context based on the incident's
 * triage selection (whatWillYouDo field).
 *
 * This context is used to display workflow-specific terminology:
 * - "136" context: Uses "Place of Safety" / "POS" terminology
 * - "mca" context: Uses "Emergency Department" / "ED" terminology
 *
 * Falls back to "136" for voluntary/other workflows until those are fully defined.
 *
 * @param incidentWorkItem - The incident work item containing triage form data
 * @returns The workflow translation context value ("136" or "mca")
 *
 * @example
 * // In a component or server handler:
 * const context = getWorkflowTranslationContext({ incidentWorkItem });
 * const label = t("recordPlaceOfSafety.title", { context });
 * // For 136: "Record Place of Safety"
 * // For MCA: "Record Emergency Department"
 */
export function getWorkflowTranslationContext({
  incidentWorkItem,
}: Partial<Pick<ActionInput, "incidentWorkItem">>):
  WorkflowTranslationContextValue | undefined {
  return incidentWorkItem
    ? getIncidentTypeTranslationContext(
        getSupportedIncidentTypeSelection({ incidentWorkItem })
          // .orTee((e) => {
          //   console.error("Unsupported incident type", e.prettifyError());
          // })
          .unwrapOr(undefined),
      )
    : undefined;
}

/**
 * An absent type, and one this build does not know (as a notification recorded
 * by a later build may hold), use the 136 wording.
 */
export function getIncidentTypeTranslationContext(
  incidentType: SupportedIncidentType | undefined,
): WorkflowTranslationContextValue {
  return match(incidentType)
    .with(
      IncidentTypeSchema.enum.mentalCapacityAct,
      () => WorkflowTranslationContext.mentalCapacityAct,
    )
    .with(
      IncidentTypeSchema.enum.section136,
      undefined,
      () => WorkflowTranslationContext.section136,
    )
    .exhaustive(() => WorkflowTranslationContext.section136);
}

/**
 * The workflow translation context values used with i18next's context feature.
 * These are used as suffixes for translation keys to provide workflow-specific
 * terminology (e.g., "Place of Safety" for 136 vs "Emergency Department" for MCA).
 */
export const WorkflowTranslationContext = {
  section136: "136",
  mentalCapacityAct: "mca",
} as const;

export type WorkflowTranslationContextValue =
  (typeof WorkflowTranslationContext)[keyof typeof WorkflowTranslationContext];
